import type { IFeeCalculationResponse } from '../../redux/checkout/types';
import type { IWalletFundOptions, IWalletFundPreset } from '../../redux/buzzbridge/types';
import { formatApiAmountLabel, formatUsdWalletCents, chargesInPaymentCurrency } from './walletFundOptionsUtils';

/** Campaign wallet fields returned by POST /mixed/sales-calc */
export interface ICampaignWalletSalesCalc extends IFeeCalculationResponse {
    wallet_credit_usd_cents?: number;
    wallet_credit_payment_cents?: number;
    payment_amount_cents?: number;
    wallet_credit_payment_input_cents?: number;
    gross_payment_input_cents?: number;
    resolved_from?: string;
    amount_earned?: number;
    fees_waived?: boolean;
    admin_test_funding?: boolean;
    admin_test_funding_usd_cents?: number;
    formatted: IFeeCalculationResponse['formatted'] & {
        wallet_credit_usd?: string;
        tax?: string;
        amount_earned?: string;
        customer_fees?: string;
    };
    breakdown: IFeeCalculationResponse['breakdown'] & {
        wallet_credit_usd_cents?: number;
        wallet_credit_payment_cents?: number;
        tax?: number;
        amount_earned?: number;
        customer_fees?: number;
    };
}

export function unwrapSalesCalcBody(res: unknown): ICampaignWalletSalesCalc | null {
    if (!res || typeof res !== 'object') return null;
    const r = res as Record<string, unknown>;
    const inner = r.data;
    if (inner && typeof inner === 'object') return inner as unknown as ICampaignWalletSalesCalc;
    return r as unknown as ICampaignWalletSalesCalc;
}

export function grossChargeCentsFromSalesCalc(fee: ICampaignWalletSalesCalc | null): number | null {
    if (!fee) return null;
    if (typeof fee.gross_payment_input_cents === 'number' && fee.gross_payment_input_cents > 0) {
        return fee.gross_payment_input_cents;
    }
    if (typeof fee.payment_amount_cents === 'number' && fee.payment_amount_cents > 0) {
        return fee.payment_amount_cents;
    }
    const b = fee.breakdown?.total_amount;
    if (typeof b === 'number' && b > 0) return b;
    if (typeof fee.total_amount === 'number' && fee.total_amount > 0) return fee.total_amount;
    return null;
}

export function resolveFeeProcessingCentsFromSalesCalc(fee: ICampaignWalletSalesCalc, gross: number | null): number {
    const fromBreakdown = fee.breakdown?.total_fees;
    if (typeof fromBreakdown === 'number' && fromBreakdown > 0) return fromBreakdown;
    if (typeof fee.total_fees === 'number' && fee.total_fees > 0) return fee.total_fees;
    const cust = fee.customer_fees;
    if (typeof cust === 'number' && cust > 0 && (gross == null || cust < gross)) return cust;
    return 0;
}

/** Net USD wallet credit (cents) from sales-calc — use for manual/custom entry only. */
export function resolveWalletCreditUsdCentsFromSalesCalc(
    fee: ICampaignWalletSalesCalc | null | undefined,
    fallbackUsdCents: number,
): number {
    if (!fee) return fallbackUsdCents;
    const bd = fee.breakdown;
    const candidates = [
        fee.wallet_credit_usd_cents,
        bd?.wallet_credit_usd_cents,
        fee.amount_earned,
        bd?.amount_earned,
        bd?.net_amount,
        fee.net_amount,
        fee.wallet_credit_cents,
    ];
    for (const value of candidates) {
        if (typeof value === 'number' && value > 0) return value;
    }
    return fallbackUsdCents;
}

/**
 * Wallet credit to display and send on fund — preset amount when user picked a preset,
 * otherwise sales-calc resolution for manual entry.
 */
export function resolveWalletCreditUsdCents(
    fee: ICampaignWalletSalesCalc | null | undefined,
    fallbackUsdCents: number,
    selectedPreset?: IWalletFundPreset | null,
): number {
    if (selectedPreset?.wallet_credit?.amount_cents) {
        return selectedPreset.wallet_credit.amount_cents;
    }
    return resolveWalletCreditUsdCentsFromSalesCalc(fee, fallbackUsdCents);
}

export function formatWalletCreditLabelFromSalesCalc(
    fee: ICampaignWalletSalesCalc | null | undefined,
    fallbackUsdCents: number,
    fundOptions?: IWalletFundOptions | null,
): string {
    const fmt = fee?.formatted;
    if (fmt?.wallet_credit_usd) {
        return formatApiAmountLabel(fmt.wallet_credit_usd, fundOptions ?? null) ?? fmt.wallet_credit_usd;
    }
    if (fmt?.net_amount?.includes('$')) {
        return formatApiAmountLabel(fmt.net_amount, fundOptions ?? null) ?? fmt.net_amount;
    }
    if (fmt?.amount_earned?.includes('$')) {
        return formatApiAmountLabel(fmt.amount_earned, fundOptions ?? null) ?? fmt.amount_earned;
    }
    return formatUsdWalletCents(resolveWalletCreditUsdCentsFromSalesCalc(fee, fallbackUsdCents));
}

export function formatWalletCreditLabel(
    fee: ICampaignWalletSalesCalc | null | undefined,
    fallbackUsdCents: number,
    fundOptions?: IWalletFundOptions | null,
    selectedPreset?: IWalletFundPreset | null,
): string {
    if (selectedPreset?.wallet_credit) {
        const fromFormatted = formatApiAmountLabel(
            selectedPreset.wallet_credit.formatted,
            fundOptions ?? null,
            selectedPreset,
        );
        if (fromFormatted) return fromFormatted;
        return formatUsdWalletCents(selectedPreset.wallet_credit.amount_cents);
    }
    return formatWalletCreditLabelFromSalesCalc(fee, fallbackUsdCents, fundOptions);
}

export function formatTotalPayLabelFromSalesCalc(
    fee: ICampaignWalletSalesCalc | null | undefined,
    fundOptions?: IWalletFundOptions | null,
    activePreset?: IWalletFundPreset | null,
): string | null {
    const raw = fee?.formatted?.total_amount;
    if (!raw) return null;
    return formatApiAmountLabel(raw, fundOptions ?? null, activePreset ?? null) ?? raw;
}

export function salesCalcPreviewIsValid(
    fee: ICampaignWalletSalesCalc | null,
    walletCreditUsdCents: number,
    fundOptions: IWalletFundOptions | null,
    paymentChargeCents?: number | null,
    selectedPreset?: IWalletFundPreset | null,
): boolean {
    if (!fee) return false;

    if (selectedPreset && (selectedPreset.fees_waived || selectedPreset.purpose === 'admin_test_funding')) {
        const expectedAmount = selectedPreset.fund_request?.amount ?? selectedPreset.wallet_credit.amount_cents;
        const total = fee.total_amount ?? grossChargeCentsFromSalesCalc(fee);
        const feesOk = fee.fees_waived === true || fee.admin_test_funding === true || fee.customer_fees === 0;
        return total === expectedAmount && feesOk;
    }

    if (selectedPreset) {
        return grossChargeCentsFromSalesCalc(fee) != null;
    }

    if (paymentChargeCents != null && paymentChargeCents >= 100 && fee.resolved_from === 'gross_payment') {
        const gross = grossChargeCentsFromSalesCalc(fee);
        return fee.gross_payment_input_cents === paymentChargeCents && gross === paymentChargeCents;
    }

    if (
        paymentChargeCents != null &&
        paymentChargeCents >= 100 &&
        typeof fee.wallet_credit_payment_input_cents === 'number'
    ) {
        return fee.wallet_credit_payment_input_cents === paymentChargeCents;
    }

    if (typeof fee.wallet_credit_usd_cents === 'number' && fee.wallet_credit_usd_cents > 0) {
        return fee.wallet_credit_usd_cents === walletCreditUsdCents;
    }

    if (typeof fee.wallet_credit_cents === 'number') {
        return fee.wallet_credit_cents === walletCreditUsdCents;
    }

    if (!chargesInPaymentCurrency(fundOptions)) {
        return fee.amount === walletCreditUsdCents;
    }

    return grossChargeCentsFromSalesCalc(fee) != null;
}
